// 22's own nav entries, behind an EXPLICIT installer (contract 22 §5/§11).
//
// Why a function and not a module side effect: `resetSettingsRegistry()` clears nav entries
// wholesale (it deliberately preserves only the host's route children), so entries registered at
// module-eval time survive exactly until the first spec that resets — and then silently vanish for
// the rest of that file. A registration that a test can re-run is the only shape that survives.
//
// `app/bootstrap.ts` calls this alongside the other registrants' `install*Settings(reg)`, before
// `createAppRouter()` seals the child-route registry. 22's own routes are declared statically in
// `routes.ts`, so only the nav half is registered here.
import { useSettingsRbac } from './composables/useSettingsRbac'
import { registerSettingsNavEntry } from './flows/settingsNavRegistry'

/**
 * Register the panes module 22 owns outright. Not idempotent on purpose: `registerSettingsNavEntry`
 * throws on a duplicate key outside production, which is exactly the signal a composition root that
 * installed twice should get.
 *
 * The identity entries are unconditionally visible — v3 had them as `active: true`
 * (`useWorkspaceSettings.js:181-185, 209-213`). Per-pane authority is a DEGRADED render inside the
 * pane (§12), never a hidden nav entry. Permissions is the exception, and v3 agreed: its tab was
 * `active: isAdminOrOwner` (`useWorkspaceSettings.js:214`), so it is `canManageMembers`-gated here
 * AND degrades to `forbidden` on a deep link — §12's defence in depth, both layers.
 */
export function installWorkspaceSettingsPanes(): void {
  registerSettingsNavEntry({
    key: 'account',
    labelKey: 'workspaceSettings.nav.basics',
    icon: 'clipboard-list',
    group: 'account',
    order: 10,
    routeName: 'SettingsAccount',
    visible: () => true,
  })

  registerSettingsNavEntry({
    key: 'permissions',
    labelKey: 'workspaceSettings.nav.permissions',
    icon: 'users',
    group: 'account',
    order: 30,
    routeName: 'SettingsPermissions',
    visible: () => useSettingsRbac().canManageMembers.value,
  })

  registerSettingsNavEntry({
    key: 'profile',
    labelKey: 'workspaceSettings.nav.profile',
    icon: 'user',
    group: 'personal',
    order: 10,
    routeName: 'SettingsProfile',
    visible: () => true,
  })
}
